import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import {
  GroupShared,
  GroupType,
  ProductType,
} from "pages/OrganizationAdmin/types";
import Group from "./Group";
import { useFormatMessage } from "components/IntlProvider";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  productData: ProductType;
  isLoaded: boolean;
  organizationCodename: string;
  refreshProduct: () => void;
}

const Groups = memo(
  ({ productData, organizationCodename, isLoaded, refreshProduct }: Props) => {
    const [searchedGroups, setSearchedGroups] = useState<GroupType[]>([]);
    const [productGroups, setProductroups] = useState<GroupType[]>([]);
    const t = useFormatMessage();

    const getUsers = (searchValue?: string) => {
      return api.resources.organization.groups
        .getGroups({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          setSearchedGroups(data.results);
        });
    };

    useEffect(() => {
      getUsers();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setProductroups(productData.shared_to_groups);
    }, [productData.shared_to_groups]);

    const addGroupToProduct = (id: number) => {
      const groups = [
        ...productData.shared_to_groups.map((group: GroupShared) => group.id),
        id,
      ];

      return api.resources.organization.products
        .updateProduct({
          organizationCodename: organizationCodename,
          id: productData.id as number,
          data: {
            shared_to_groups: groups,
          },
        })
        .then(() => {
          refreshProduct();
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.product", "Product"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };

    const removeGroupFromProduct = (id: number) => {
      const groups = productData.shared_to_groups.filter(
        (user: GroupShared) => user.id !== id
      );

      return api.resources.organization.products
        .updateProduct({
          organizationCodename: organizationCodename,
          id: productData.id as number,
          data: {
            shared_to_groups: groups.map((group: GroupShared) => group.id),
          },
        })
        .then(() => {
          refreshProduct();
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.product", "Product"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };
    const options = searchedGroups.map((groupData: GroupType) => ({
      id: groupData.id,
      label: groupData.name,
    }));

    const filteredOptions = useMemo(() => {
      return options.filter((searchedUser: { id: number; label: string }) => {
        const itemIndex = productGroups.findIndex((user) => {
          return user.id === searchedUser.id;
        });

        return itemIndex === -1;
      });
    }, [productGroups, options]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.addTo", "Add {x} to {y}", {
            y: t("organization.product", "Product"),
            x: t("organization.groups", "Groups"),
          })}
        </Typography>
        <Search
          options={filteredOptions.map(({ id, label }) => ({
            value: id,
            label,
          }))}
          styles={{
            maxWidth: "450px",
          }}
          name="group"
          onChange={(_name, user) => {
            if (user) {
              addGroupToProduct(user.value);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.groups", "groups"),
          })}
          asyncSeach={(searchedValue: string) => getUsers(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.sharedWith", "{x} shared with {y}", {
            x: t("organization.product", "Product"),
            y: t("organization.groups", "Groups"),
          })}
          :
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={productData.shared_to_groups.length > 0}
            noDataText={t(
              "noresults",
              "No results. We have nothing to show here."
            )}
          >
            {productGroups.map((group) => {
              return (
                <Group
                  name={group.name}
                  id={group.id}
                  key={group.id}
                  removeGroupFromProject={removeGroupFromProduct}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Groups;
